import React, { useRef, useEffect, useState } from 'react';
import {
    makeStyles,
    Card,
    CardContent,
    Modal,
    Typography,
    OutlinedInput,
    InputAdornment,
    IconButton,
    TextField,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { IRejectModal } from './types';
import SendIcon from '@material-ui/icons/Send';
import useSubstatus from 'src/hooks/useSubstatus';
import { STATUSES } from 'src/constants/mongo/status';
import { ISubstatus } from 'src/interfaces/substatus';
import { filterSubstatus } from 'src/utils/status/filterSubstatus';

const useStyles = makeStyles((theme) => ({
    root: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        overflowY: 'auto',
        paddingTop: 20,
        paddingBottom: 20,
        minWidth: 800,
    },
    button: {
        height: 55,
    },
    reject: {
        backgroundColor: theme.palette.error.main,
        '&:hover': {
            backgroundColor: theme.palette.error.dark,
        },
    },
    actionsContainer: {
        marginTop: 20,
    },
    resetContainer: {
        padding: theme.spacing(3),
    },
    message: {
        textAlign: 'center',
        marginBottom: '1em',
    },
}));

export const RejectModal = ({
    currentSubstatus,
    setCurrentSubstatus,
    comment,
    setComment,
    open,
    setOpen,
    handleReject,
    ...props
}: IRejectModal) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const forwardedRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const { substatuses } = useSubstatus();
    const [substatusesFiltered, setSubstatusesFiltered] = useState<
        ISubstatus[]
    >([]);

    useEffect(() => {
        if (open) {
            const substatusesFiltered = filterSubstatus(
                STATUSES.REJECTED.ID,
                substatuses
            );
            setSubstatusesFiltered(substatusesFiltered);
            setCurrentSubstatus(substatusesFiltered[0]?._id || '');
        }
    }, [open, substatuses]);

    const handleSubmit = () => inputRef.current?.value && handleReject();

    const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) =>
        event.keyCode === 13 && handleSubmit();

    return (
        <Modal
            open={open}
            ref={forwardedRef}
            onClose={() => setOpen(false)}
            {...props}
        >
            <Card className={classes.root}>
                <CardContent>
                    <Typography
                        variant="h2"
                        color="textPrimary"
                        className={classes.message}
                    >
                        {t('Messages.RejectMessage')}
                    </Typography>
                    <TextField
                        fullWidth
                        label={t('Substatus.Substatus')}
                        value={currentSubstatus}
                        onChange={(event) =>
                            setCurrentSubstatus(event.target.value)
                        }
                        onKeyUp={handleKeyUp}
                        select
                        SelectProps={{
                            native: true,
                        }}
                        variant="outlined"
                        style={{ marginBottom: '1em' }}
                        name="substatus"
                        InputLabelProps={{
                            shrink: true,
                        }}
                    >
                        {substatusesFiltered.map((substatus: ISubstatus) => (
                            <option key={substatus._id} value={substatus._id}>
                                {t(`Substatus.${substatus.name}`)}
                            </option>
                        ))}
                    </TextField>
                    <OutlinedInput
                        inputRef={inputRef}
                        placeholder={t('Messages.RejectComment')}
                        fullWidth
                        value={comment}
                        onChange={(event) => setComment(event.target.value)}
                        onKeyUp={handleKeyUp}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton onClick={handleSubmit}>
                                    <SendIcon />
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                </CardContent>
            </Card>
        </Modal>
    );
};
